import isNil from 'lodash/isNil';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { APIClient as ScaffoldClient, FetchUserResponse, SignOutUserResponse } from '@groma-dev/scaffold';
import { AppConfig } from '../AppConfig';
import {
  Account,
  FundType,
  BankAccount,
  Claim,
  ClaimStatus,
  ExternalWalletLink,
  ExternalWalletLinkState,
} from '../models';
import { APIClient } from '../apiclient/APIClient';
import {
  CreateExternalWalletChallengeRequest,
  CreateExternalWalletChallengeResponse,
  CreateExternalWalletLinkRequest,
  CreateExternalWalletLinkResponse,
  CreateManualPaymentMethodRequest,
  CreateManualPaymentMethodResponse,
  CreateRedemptionRequestRequest,
  CreateRedemptionRequestResponse,
  DeleteExternalWalletLinkRequest,
  DeleteExternalWalletLinkResponse,
  DeletePaymentMethodRequest,
  DeletePaymentMethodResponse,
  ExternalWalletOperation,
  FetchAccountDocumentsRequest,
  FetchAccountDocumentsResponse,
  FetchAccountHoldingsResponse,
  FetchAccountPaymentMethodsRequest,
  FetchAccountPaymentMethodsResponse,
  FetchAccountRequest,
  FetchAccountResponse,
  FetchAccountsRequest,
  FetchAccountsResponse,
  FetchAccountTransactionsRequest,
  FetchAccountTransactionsResponse,
  FetchBankAccountResponse,
  FetchExternalWalletsRequest,
  FetchExternalWalletsResponse,
  FetchWalletBalanceResponse,
  PaymentMethod,
  SetPreferredPaymentMethodRequest,
  SetPreferredPaymentMethodResponse,
  UpdateBankAccountRequest,
  UpdateBankAccountResponse,
  UpdateManualPaymentMethodRequest,
  UpdateManualPaymentMethodResponse,
  WatchExternalWalletLinkRequest,
  FetchPlaidLinkTokenResponse,
  CreatePlaidBankAccountRequest,
  CreatePlaidBankAccountResponse,
  GetClaimResponse,
  ClaimCoinResponse,
  GetOverviewResponse,
  GetEcosystemOverviewResponse,
  RequestInvitationResponse,
  VerifyClaimOwnerResponse,
  JoinWaitlistResponse,
} from '../apiclient/APIClientInterface';
import { LocalStorage } from '../utils/Constants';
import {
  AccountDocumentsFetchingStateInterface,
  AccountHoldingsStateInterface,
  AccountsStateInterface,
  AccountStateInterface,
  AccountTransactionsInterface,
  AppState,
  ScaffoldClientHeaders,
  UserStateInterface,
  WalletBalanceStateInterface,
  BankAccountStateInterface,
  GromaCoinStateInterface,
  ExternalWalletsStateInterface,
} from './DataStoreInterface';

const appInitialState: AppState = {
  initialized: false,
  nonREITAccount: false,
};

const userInitialState: UserStateInterface = {
  fetchingUser: false,
  user: undefined,
};

const accountsInitialState: AccountsStateInterface = {
  accounts: undefined,
  isFetching: false,
};

const accountInitialState: AccountStateInterface = {
  account: undefined,
  isFetching: false,
  bankAccount: undefined,
  isBankAccountFetching: false,
  paymentMethods: [],
  isPaymentMethodsFetching: false,
};

const accountHoldingsInitialState: AccountHoldingsStateInterface = {
  isFetching: false,
};

const walletBalanceInitialState: WalletBalanceStateInterface = {
  isFetching: false,
};

const accountDocumentsInitialFetchingState: AccountDocumentsFetchingStateInterface = {};

const accountTransactionsInitialState: AccountTransactionsInterface = {
  isFetching: false,
};

const bankAccountInitialState: BankAccountStateInterface = {
  isFetching: false,
};

const gromaCoinInitialState: GromaCoinStateInterface = {
  claimId: null,
  claim: null,
  overview: null,
  ecosystemOverview: null,
  loading: false,
  error: null,
  invitationSent: false,
  invitationLoading: false,
  invitationError: null,
  accountAlreadyActive: false,
};

const externalWalletsInitialState: ExternalWalletsStateInterface = {
  links: [],
  isFetching: false,
  hasFetchFailed: false,
  hasPollingLapsed: false,
};

export const externalWalletPollIntervalMilliseconds = 15000;
export const externalWalletPollFirstTickMilliseconds = 4000;
export const externalWalletPollAttemptLimit = 40;
export const externalWalletSeedMaxAgeMilliseconds = 60000;

export const externalWalletPendingStates: ExternalWalletLinkState[] = ['pending_registry', 'pending_unlink'];

export const isDemoMode = () => {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    return params.get('demo') === 'true';
  }
  return false;
};

const demoClaim: Claim = {
  id: 'demo-claim-123',
  email: null,
  wallet_address: '0x742d35Cc6634C0532925a3b844Bc9e7595f8bE2a',
  status: ClaimStatus.UNCLAIMED,
  transaction_hash: null,
  basescan_url: null,
  transaction_status: null,
};

const demoOverview = {
  name: 'GromaCoin',
  symbol: 'GRO',
  current_price_per: { amount: '1.05', currency: 'USD' },
  market_capitalization: { amount: '150000000', currency: 'USD' },
  estimated_net_asset_value_per_share: { amount: '1.03', currency: 'USD' },
};

const demoEcosystemOverview = {
  property_count: 123,
  current_reit_statement: {
    property_count: 86,
    total_asset_value: { amount: '103170209', currency: 'USD' },
  },
};

export default class DataStore {
  private static instance: any = null;

  @observable
  appState = appInitialState;

  @observable
  userState = userInitialState;

  @observable
  accountsState = accountsInitialState;

  @observable
  accountState = accountInitialState;

  @observable
  accountHoldingsState = accountHoldingsInitialState;

  @observable
  walletBalanceState = walletBalanceInitialState;

  @observable
  bankAccountState = bankAccountInitialState;

  @observable
  accountDocumentsFetchingState = accountDocumentsInitialFetchingState;

  @observable
  accountTransactions = accountTransactionsInitialState;

  @observable
  gromaCoinState = gromaCoinInitialState;

  @observable
  externalWalletsState = externalWalletsInitialState;

  private pollingTimer: ReturnType<typeof setInterval> | null = null;

  private demoTimeouts: ReturnType<typeof setTimeout>[] = [];

  private demoClaimState: Claim = { ...demoClaim };

  private externalWalletPollingTimer: ReturnType<typeof setInterval> | null = null;

  private externalWalletFirstTickTimer: ReturnType<typeof setTimeout> | null = null;

  private externalWalletPollAttempts = 0;

  private externalWalletWatchedLinkId: number | null = null;

  private externalWalletWatchedOperation: ExternalWalletOperation | null = null;

  private externalWalletPollingSession = 0;

  private externalWalletFetchSequence = 0;

  private externalWalletSeededLink: ExternalWalletLink | null = null;

  private externalWalletSeededLinkAt = 0;

  get isExternalWalletPollingActive() {
    return this.externalWalletPollingTimer !== null;
  }

  constructor() {
    if (isNil(DataStore.instance)) {
      DataStore.instance = this;
    }

    makeObservable(this);

    if (isDemoMode()) {
      this.gromaCoinState.claimId = 'demo-claim-123';
    }

    // eslint-disable-next-line no-constructor-return
    return DataStore.instance;
  }

  @action
  createRedemptionRequest = async (
    request: CreateRedemptionRequestRequest
  ): Promise<CreateRedemptionRequestResponse> => {
    return APIClient.createRedemptionRequest(request);
  };

  @action
  fetchAccount = async (request: FetchAccountRequest): Promise<FetchAccountResponse> => {
    this.setFetchingAccount(true);

    const response = await APIClient.fetchAccount(request);
    if (response.success) {
      this.setAccount(response.account);
      this.setLocalStorage(LocalStorage.InvestmentAccount, `${response.account?.id}`);
    }

    this.setFetchingAccount(false);
    this.setAppInitialized(true);

    return response;
  };

  @action
  fetchAccountDocuments = async (request: FetchAccountDocumentsRequest): Promise<FetchAccountDocumentsResponse> => {
    this.setFetchingAccountDocumentsByCat(request.category, true);

    const response = await APIClient.fetchAccountDocuments(request);

    this.setFetchingAccountDocumentsByCat(request.category, false);

    return response;
  };

  @action
  fetchAccountHoldings = async (request: { id: number | string }): Promise<FetchAccountHoldingsResponse> => {
    this.setFetchingAccountHoldings(true);

    const response = await APIClient.fetchAccountHoldings({ id: String(request.id) });

    this.setFetchingAccountHoldings(false);

    return response;
  };

  @action
  fetchAccounts = async (request: FetchAccountsRequest): Promise<FetchAccountsResponse> => {
    this.setFetchingAccounts(true);
    const response = await APIClient.fetchAccounts(request);
    this.setFetchingAccounts(false);

    return response;
  };

  @action
  fetchAccountTransactions = async (
    request: FetchAccountTransactionsRequest
  ): Promise<FetchAccountTransactionsResponse> => {
    this.setFetchingAccountTransactions(true);

    const response = await APIClient.fetchAccountTransactions(request);

    this.setFetchingAccountTransactions(false);

    return response;
  };

  @action
  fetchBankAccount = async (request: { id: number | string }): Promise<FetchBankAccountResponse> => {
    this.setFetchingBankAccount(true);
    const response = await APIClient.fetchBankAccount({ id: String(request.id) });

    if (response.success) {
      this.setBankAccount(response.bankAccount);
    } else if (response.status === 404) {
      this.setBankAccount(null);
    }

    this.setFetchingBankAccount(false);

    return response;
  };

  @action
  fetchPlaidLinkToken = async (): Promise<FetchPlaidLinkTokenResponse> => {
    const response = await APIClient.fetchPlaidLinkToken({});
    return response;
  };

  @action
  createPlaidBankAccount = async (request: CreatePlaidBankAccountRequest): Promise<CreatePlaidBankAccountResponse> => {
    const response = await APIClient.createPlaidBankAccount(request);
    return response;
  };

  @action
  createManualPaymentMethod = async (
    request: CreateManualPaymentMethodRequest
  ): Promise<CreateManualPaymentMethodResponse> => {
    const response = await APIClient.createManualPaymentMethod(request);
    return response;
  };

  @action
  deletePaymentMethod = async (request: DeletePaymentMethodRequest): Promise<DeletePaymentMethodResponse> => {
    const response = await APIClient.deletePaymentMethod(request);
    return response;
  };

  @action
  fetchAccountPaymentMethods = async (
    request: FetchAccountPaymentMethodsRequest
  ): Promise<FetchAccountPaymentMethodsResponse> => {
    this.setFetchingPaymentMethods(true);
    const response = await APIClient.fetchAccountPaymentMethods(request);

    if (response.success) {
      this.setPaymentMethods(response.paymentMethods);
    } else {
      this.setPaymentMethods([]);
    }

    this.setFetchingPaymentMethods(false);

    return response;
  };

  @action
  setPreferredPaymentMethod = async (
    request: SetPreferredPaymentMethodRequest
  ): Promise<SetPreferredPaymentMethodResponse> => {
    const response = await APIClient.setPreferredPaymentMethod(request);
    return response;
  };

  @action
  updateManualPaymentMethod = async (
    request: UpdateManualPaymentMethodRequest
  ): Promise<UpdateManualPaymentMethodResponse> => {
    const response = await APIClient.updateManualPaymentMethod(request);
    return response;
  };

  @action
  fetchUser = async (): Promise<FetchUserResponse> => {
    this.setFetchingUser(true);
    const { baseUrl } = AppConfig.Settings.Server;
    const response: FetchUserResponse = await ScaffoldClient.fetchUser({
      baseURL: baseUrl,
      headers: ScaffoldClientHeaders,
    });

    if (response.success) this.setUser(response.user);

    this.setFetchingUser(false);

    return response;
  };

  @action
  fetchWalletBalance = async (request: { id: number | string }): Promise<FetchWalletBalanceResponse> => {
    this.setFetchingWalletBalance(true);

    const response = await APIClient.fetchWalletBalance({ id: String(request.id) });

    this.setFetchingWalletBalance(false);

    return response;
  };

  getLocalStorage = (key: string) => {
    return localStorage.getItem(key);
  };

  @action
  setAccount = (account?: Account) => {
    this.accountState.account = account;
    if (!account?.fund || account?.fund === FundType.FUND_TWO) this.setNonREITAccount(false);
    else this.setNonREITAccount(true);
  };

  @action
  setAccounts = (accounts?: Account[]) => {
    this.accountsState.accounts = accounts;
  };

  @action
  setAppInitialized = (value: boolean) => {
    this.appState.initialized = value;
  };

  @action
  setBankAccount = (bankAccount: BankAccount | null) => {
    this.accountState.bankAccount = bankAccount;
  };

  @action
  setExternalWalletFetchFailed = (value: boolean) => {
    this.externalWalletsState.hasFetchFailed = value;
  };

  @action
  setExternalWalletLink = (link: ExternalWalletLink) => {
    const { links } = this.externalWalletsState;
    const index = links.findIndex(existing => existing.id === link.id);

    this.externalWalletsState.links =
      index === -1 ? [...links, link] : links.map(existing => (existing.id === link.id ? link : existing));
    this.externalWalletsState.hasFetchFailed = false;
    this.externalWalletSeededLink = link;
    this.externalWalletSeededLinkAt = Date.now();
  };

  @action
  setExternalWalletLinks = (links: ExternalWalletLink[]) => {
    this.externalWalletsState.links = links;
    this.externalWalletsState.hasFetchFailed = false;
  };

  @action
  setExternalWalletPollingLapsed = (value: boolean) => {
    this.externalWalletsState.hasPollingLapsed = value;
  };

  @action
  setFetchingAccount = (value: boolean) => {
    this.accountState.isFetching = value;
  };

  @action
  setFetchingAccountDocumentsByCat = (cat: string, value: boolean) => {
    this.accountDocumentsFetchingState[cat] = value;
  };

  @action
  setFetchingAccountHoldings = (value: boolean) => {
    this.accountHoldingsState.isFetching = value;
  };

  @action
  setFetchingAccounts = (value: boolean) => {
    this.accountsState.isFetching = value;
  };

  @action
  setFetchingAccountTransactions = (value: boolean) => {
    this.accountTransactions.isFetching = value;
  };

  @action
  setFetchingBankAccount = (value: boolean) => {
    this.bankAccountState.isFetching = value;
  };

  @action
  setFetchingExternalWallets = (value: boolean) => {
    this.externalWalletsState.isFetching = value;
  };

  @action
  setFetchingPaymentMethods = (value: boolean) => {
    this.accountState.isPaymentMethodsFetching = value;
  };

  @action
  setGromaCoinClaimId = (id: string | null) => {
    this.gromaCoinState.claimId = id;
  };

  @action
  setGromaCoinLoading = (value: boolean) => {
    this.gromaCoinState.loading = value;
  };

  @action
  setGromaCoinError = (error: string | null) => {
    this.gromaCoinState.error = error;
  };

  @action
  fetchGromaCoinClaim = async (): Promise<GetClaimResponse | undefined> => {
    if (isDemoMode()) {
      runInAction(() => {
        this.gromaCoinState.claim = { ...this.demoClaimState };
        this.gromaCoinState.loading = false;
      });
      return { success: true, claim: this.demoClaimState } as GetClaimResponse;
    }

    if (!this.gromaCoinState.claimId) return undefined;

    this.setGromaCoinLoading(true);
    this.setGromaCoinError(null);

    const response = await APIClient.getClaim({ claimId: this.gromaCoinState.claimId });

    runInAction(() => {
      if (response.success && response.claim) {
        this.gromaCoinState.claim = response.claim;
      } else {
        this.gromaCoinState.error = response.error?.message ?? 'Failed to load claim.';
      }
      this.gromaCoinState.loading = false;
    });

    return response;
  };

  @action
  submitGromaCoinClaim = async (email: string): Promise<ClaimCoinResponse | undefined> => {
    if (isDemoMode()) {
      this.setGromaCoinLoading(true);

      APIClient.joinWaitlist({ email, source: 'demo' }).catch(() => {});

      await new Promise(resolve => setTimeout(resolve, 800));

      this.demoClaimState = {
        ...this.demoClaimState,
        email,
        status: ClaimStatus.PENDING,
        transaction_hash: `0x${Math.random().toString(16).slice(2, 66)}`,
        basescan_url: 'https://basescan.org/tx/0x123',
      };

      runInAction(() => {
        this.gromaCoinState.claim = { ...this.demoClaimState };
        this.gromaCoinState.loading = false;
      });

      return { success: true, claim: this.demoClaimState } as ClaimCoinResponse;
    }

    if (!this.gromaCoinState.claimId) return undefined;

    this.setGromaCoinLoading(true);
    this.setGromaCoinError(null);

    const response = await APIClient.claimCoin({ claimId: this.gromaCoinState.claimId, email });

    runInAction(() => {
      if (response.success && response.claim) {
        this.gromaCoinState.claim = response.claim;
      } else {
        this.gromaCoinState.error = response.error?.message ?? 'Failed to submit claim.';
      }
      this.gromaCoinState.loading = false;
    });

    return response;
  };

  @action
  fetchGromaCoinOverview = async (): Promise<GetOverviewResponse> => {
    if (isDemoMode()) {
      runInAction(() => {
        this.gromaCoinState.overview = demoOverview;
      });
      return { success: true, overview: demoOverview } as GetOverviewResponse;
    }

    const response = await APIClient.getOverview({});

    runInAction(() => {
      if (response.success && response.overview) {
        this.gromaCoinState.overview = response.overview;
      }
    });

    return response;
  };

  @action
  fetchGromaCoinEcosystemOverview = async (): Promise<GetEcosystemOverviewResponse> => {
    if (isDemoMode()) {
      runInAction(() => {
        this.gromaCoinState.ecosystemOverview = demoEcosystemOverview;
      });
      return { success: true, ecosystemOverview: demoEcosystemOverview } as GetEcosystemOverviewResponse;
    }

    const response = await APIClient.getEcosystemOverview({});

    runInAction(() => {
      if (response.success && response.ecosystemOverview) {
        this.gromaCoinState.ecosystemOverview = response.ecosystemOverview;
      }
    });

    return response;
  };

  @action
  startGromaCoinPolling = () => {
    this.stopGromaCoinPolling();

    const demoBlockNumber = 21934000 + Math.floor(Math.random() * 1000);

    if (isDemoMode()) {
      this.gromaCoinState.claim = { ...this.demoClaimState };

      this.demoTimeouts.push(
        setTimeout(() => {
          this.demoClaimState = {
            ...this.demoClaimState,
            transaction_hash: '0x8a7d953f2b6e4c1a9d0e3f5b7c2a4d6e8f0a1b3c5d7e9f0a2b4c6d8e0f1a3b5c7',
            basescan_url: 'https://basescan.org/tx/0x8a7d953f2b6e4c1a9d0e3f5b7c2a4d6e8f0a1b3c5d7e9f0a2b4c6d8e0f1a3b5c7',
          };
          runInAction(() => {
            this.gromaCoinState.claim = { ...this.demoClaimState };
          });
        }, 1000)
      );

      this.demoTimeouts.push(
        setTimeout(() => {
          this.demoClaimState = {
            ...this.demoClaimState,
            transaction_status: {
              status: 'pending',
              block_number: demoBlockNumber,
              confirmations: 1,
            },
          };
          runInAction(() => {
            this.gromaCoinState.claim = { ...this.demoClaimState };
          });
        }, 2500)
      );

      this.demoTimeouts.push(
        setTimeout(() => {
          this.demoClaimState = {
            ...this.demoClaimState,
            status: ClaimStatus.COMPLETED,
            transaction_status: {
              status: 'confirmed',
              block_number: demoBlockNumber,
              confirmations: 1,
            },
          };
          runInAction(() => {
            this.gromaCoinState.claim = { ...this.demoClaimState };
          });
        }, 6000)
      );

      return;
    }

    this.pollingTimer = setInterval(async () => {
      await this.fetchGromaCoinClaim();

      const { claim } = this.gromaCoinState;
      if (claim && (claim.status === ClaimStatus.COMPLETED || claim.status === ClaimStatus.FAILED)) {
        this.stopGromaCoinPolling();
      }
    }, 2000);
  };

  stopGromaCoinPolling = () => {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.demoTimeouts.forEach(t => clearTimeout(t));
    this.demoTimeouts = [];
  };

  @action
  requestGromaCoinInvitation = async (): Promise<RequestInvitationResponse | undefined> => {
    if (isDemoMode()) {
      runInAction(() => {
        this.gromaCoinState.invitationLoading = true;
      });

      await new Promise(resolve => setTimeout(resolve, 1000));
      runInAction(() => {
        this.gromaCoinState.invitationSent = true;
        this.gromaCoinState.invitationLoading = false;
      });
      return { success: true, message: 'Invitation sent. Please check your email.' } as RequestInvitationResponse;
    }

    if (!this.gromaCoinState.claimId) return undefined;

    runInAction(() => {
      this.gromaCoinState.invitationLoading = true;
      this.gromaCoinState.invitationError = null;
      this.gromaCoinState.accountAlreadyActive = false;
    });

    const response = await APIClient.requestInvitation({ claimId: this.gromaCoinState.claimId });

    runInAction(() => {
      if (response.success) {
        this.gromaCoinState.invitationSent = true;
      } else {
        const errorCode = (response.error as any)?.details?.code;
        if (errorCode === 'user_already_active' || errorCode === 'invitation_already_accepted') {
          this.gromaCoinState.accountAlreadyActive = true;
        } else {
          this.gromaCoinState.invitationError = response.error?.message ?? 'Failed to send invitation.';
        }
      }
      this.gromaCoinState.invitationLoading = false;
    });

    return response;
  };

  @action
  verifyGromaCoinClaimOwner = async (email: string): Promise<VerifyClaimOwnerResponse | undefined> => {
    if (isDemoMode()) {
      this.setGromaCoinLoading(true);

      await new Promise(resolve => setTimeout(resolve, 800));

      runInAction(() => {
        this.gromaCoinState.claim = { ...this.demoClaimState, email, status: ClaimStatus.COMPLETED };
        this.gromaCoinState.loading = false;
      });

      return { success: true, claim: this.gromaCoinState.claim } as VerifyClaimOwnerResponse;
    }

    if (!this.gromaCoinState.claimId) return undefined;

    this.setGromaCoinLoading(true);
    this.setGromaCoinError(null);

    const response = await APIClient.verifyClaimOwner({ claimId: this.gromaCoinState.claimId, email });

    runInAction(() => {
      if (response.success && response.claim) {
        this.gromaCoinState.claim = response.claim;
      } else {
        this.gromaCoinState.error = response.error?.message ?? 'Email does not match claim owner.';
      }
      this.gromaCoinState.loading = false;
    });

    return response;
  };

  @action
  joinGromaCoinWaitlist = async (email: string): Promise<JoinWaitlistResponse> => {
    if (isDemoMode()) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true } as JoinWaitlistResponse;
    }

    const response = await APIClient.joinWaitlist({
      email,
      claimId: this.gromaCoinState.claimId ?? undefined,
    });
    return response;
  };

  @action
  createExternalWalletChallenge = async (
    request: CreateExternalWalletChallengeRequest
  ): Promise<CreateExternalWalletChallengeResponse> => {
    return APIClient.createExternalWalletChallenge(request);
  };

  @action
  createExternalWalletLink = async (
    request: CreateExternalWalletLinkRequest
  ): Promise<CreateExternalWalletLinkResponse> => {
    return APIClient.createExternalWalletLink(request);
  };

  @action
  deleteExternalWalletLink = async (
    request: DeleteExternalWalletLinkRequest
  ): Promise<DeleteExternalWalletLinkResponse> => {
    return APIClient.deleteExternalWalletLink(request);
  };

  @action
  fetchExternalWallets = async (request: FetchExternalWalletsRequest): Promise<FetchExternalWalletsResponse> => {
    this.externalWalletFetchSequence += 1;

    const fetchId = this.externalWalletFetchSequence;
    const session = this.externalWalletPollingSession;

    this.setFetchingExternalWallets(true);

    const response = await APIClient.fetchExternalWallets(request);

    const selectedAccountId = this.accountState.account?.id;
    const isForSelectedAccount = isNil(selectedAccountId) || `${selectedAccountId}` === request.investmentAccountId;
    const isForCurrentSession = session === this.externalWalletPollingSession;

    if (isForSelectedAccount && isForCurrentSession) {
      if (response.success) {
        this.setExternalWalletLinks(this.externalWalletLinksWithSeed(response.externalWalletLinks ?? []));
      } else {
        this.setExternalWalletFetchFailed(true);
      }
    }

    if (fetchId === this.externalWalletFetchSequence) {
      this.setFetchingExternalWallets(false);
    }

    return response;
  };

  pollExternalWalletsOnce = async (request: FetchExternalWalletsRequest) => {
    if (this.externalWalletsState.isFetching) return;

    const session = this.externalWalletPollingSession;

    this.externalWalletPollAttempts += 1;

    const response = await this.fetchExternalWallets(request);

    if (session !== this.externalWalletPollingSession) return;

    if (this.externalWalletPollAttempts >= externalWalletPollAttemptLimit) {
      const serverLinks = response.success ? response.externalWalletLinks ?? [] : null;

      this.stopExternalWalletPolling();
      this.externalWalletSeededLink = null;
      this.setExternalWalletPollingLapsed(!this.externalWalletWorkIsDone(serverLinks));
      return;
    }

    if (!response.success) return;

    if (this.externalWalletWorkIsDone(response.externalWalletLinks ?? [])) {
      this.stopExternalWalletPolling();
    }
  };

  @action
  resetExternalWallets = () => {
    this.stopExternalWalletPolling();
    this.externalWalletPollingSession += 1;
    this.externalWalletWatchedLinkId = null;
    this.externalWalletWatchedOperation = null;
    this.externalWalletSeededLink = null;
    this.externalWalletsState = { ...externalWalletsInitialState };
  };

  @action
  resumeExternalWalletPolling = (request: FetchExternalWalletsRequest) => {
    if (this.isExternalWalletPollingActive) return;

    const hasPendingLink = this.externalWalletsState.links.some(link =>
      externalWalletPendingStates.includes(link.state)
    );

    if (!hasPendingLink) return;

    this.externalWalletWatchedLinkId = null;
    this.externalWalletWatchedOperation = null;
    this.startExternalWalletPollingSession(request);
  };

  @action
  watchExternalWalletLink = (request: WatchExternalWalletLinkRequest) => {
    this.externalWalletWatchedLinkId = request.linkId;
    this.externalWalletWatchedOperation = request.operation;
    this.startExternalWalletPollingSession({ investmentAccountId: request.investmentAccountId });
  };

  stopExternalWalletPolling = () => {
    if (this.externalWalletFirstTickTimer) {
      clearTimeout(this.externalWalletFirstTickTimer);
      this.externalWalletFirstTickTimer = null;
    }

    if (this.externalWalletPollingTimer) {
      clearInterval(this.externalWalletPollingTimer);
      this.externalWalletPollingTimer = null;
    }
  };

  private externalWalletLinksWithSeed = (links: ExternalWalletLink[]) => {
    const seed = this.externalWalletSeededLink;

    if (isNil(seed)) return links;

    const seedHasLapsed = Date.now() - this.externalWalletSeededLinkAt > externalWalletSeedMaxAgeMilliseconds;

    if (seedHasLapsed || links.some(link => link.id === seed.id)) {
      this.externalWalletSeededLink = null;
      return links;
    }

    return [...links, seed];
  };

  private startExternalWalletPollingSession = (request: FetchExternalWalletsRequest) => {
    this.stopExternalWalletPolling();
    this.externalWalletPollingSession += 1;
    this.externalWalletPollAttempts = 0;
    this.setExternalWalletPollingLapsed(false);

    this.externalWalletFirstTickTimer = setTimeout(() => {
      this.pollExternalWalletsOnce(request);
    }, externalWalletPollFirstTickMilliseconds);

    this.externalWalletPollingTimer = setInterval(() => {
      this.pollExternalWalletsOnce(request);
    }, externalWalletPollIntervalMilliseconds);
  };

  private externalWalletWatchIsComplete = (serverLinks: ExternalWalletLink[]) => {
    const watched = serverLinks.find(link => link.id === this.externalWalletWatchedLinkId);

    if (this.externalWalletWatchedOperation === 'unlink') {
      return isNil(watched) || watched.state === 'unlinked' || watched.state === 'failed';
    }

    return !isNil(watched) && (watched.state === 'linked' || watched.state === 'failed');
  };

  private externalWalletWorkIsDone = (serverLinks: ExternalWalletLink[] | null) => {
    if (isNil(serverLinks)) return false;

    if (!isNil(this.externalWalletWatchedLinkId) && !this.externalWalletWatchIsComplete(serverLinks)) return false;

    return !serverLinks.some(link => externalWalletPendingStates.includes(link.state));
  };

  @action
  setPaymentMethods = (paymentMethods: PaymentMethod[]) => {
    this.accountState.paymentMethods = paymentMethods;
  };

  @action
  setFetchingUser = (value: boolean) => {
    this.userState.fetchingUser = value;
  };

  @action
  setFetchingWalletBalance = (value: boolean) => {
    this.walletBalanceState.isFetching = value;
  };

  setLocalStorage = (key: string, value: string) => {
    localStorage.setItem(key, value);
  };

  @action
  setNonREITAccount = (value: boolean) => {
    this.appState.nonREITAccount = value;
  };

  @action
  setUser = (user?: any) => {
    this.userState.user = user;
  };

  @action
  signOutUser = async (): Promise<SignOutUserResponse> => {
    const { baseUrl, timeout } = AppConfig.Settings.Server;
    const response = await ScaffoldClient.signOutUser({
      baseURL: baseUrl,
      headers: ScaffoldClientHeaders,
      timeout,
    });
    return response;
  };

  @action
  updateBankAccount = async (request: UpdateBankAccountRequest): Promise<UpdateBankAccountResponse> => {
    const response = await APIClient.updateBankAccount(request);

    return response;
  };
}
